import React from 'react';
import { STATUS, FONT_MONO, statusToSemantic } from 'Utils/styleTokens';
import StepsTable, { pairWithLatestRun, stepStatusLabel } from './StepsTable';
import { typeLabel } from './groupTypes';

// A run whose steps all share (source, inventory) renders fan-style in the collapsed
// header: "source · inventory → dest-chips" (or "— in place"). Mixed multi-source runs
// keep the plain description line and the ordered steps table tells their story.
const fanShapeOf = (group) => {
  const steps = group.steps || [];
  if (steps.length === 0) return null;
  const first = steps[0];
  const homogeneous = steps.every(
    (s) => s.dataSourceId === first.dataSourceId && s.inventoryId === first.inventoryId
  );
  if (!homogeneous) return null;
  return {
    sourceName: first.dataSourceName || `#${first.dataSourceId}`,
    inventoryName: first.inventoryName || (first.inventoryId != null ? `#${first.inventoryId}` : ''),
    inPlace: steps.every((s) => s.targetDataSourceId == null),
  };
};

const fmtWhen = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return `${d.getMonth() + 1}/${d.getDate()} ${d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
};

// Group-level pill: latest run's status, or Idle when the group never ran.
const groupStatus = (group) => {
  if (!group.latestRun) return { label: 'IDLE', semantic: 'neutral' };
  const s = group.latestRun.status;
  return { label: s, semantic: statusToSemantic(s) };
};

function GroupCard({
  group,
  expanded,
  onToggle,
  onRun,
  onCancel,
  onDelete,
  onEdit,
  onReplaceSteps,
  onAddStep,
  onEditStep,
}) {
  const status = groupStatus(group);
  const pill = STATUS[status.semantic] || STATUS.neutral;
  const stepCount = (group.steps || []).length;
  const metaCell = (label, value) => (
    <div className='text-right'>
      <div
        className='sg-small'
        style={{ color: 'var(--ink-400)', textTransform: 'uppercase', letterSpacing: '.08em', fontSize: 10, fontWeight: 600 }}
      >
        {label}
      </div>
      <div style={{ fontSize: 12, color: 'var(--ink-700)' }}>{value}</div>
    </div>
  );

  return (
    <div
      className='sg-card mb-3'
      style={{ padding: 0, overflow: 'hidden', borderColor: expanded ? 'var(--brand-blue, #315DFF)' : undefined }}
      data-testid={`job-group-${group.id}`}
    >
      <div
        role='button'
        aria-expanded={expanded}
        onClick={onToggle}
        className='d-flex align-items-center'
        style={{ gap: 14, padding: '14px 20px', cursor: 'pointer' }}
      >
        <span className='sg-small' style={{ color: 'var(--ink-400)', width: 14 }}>
          {expanded ? '▾' : '▸'}
        </span>
        <div style={{ minWidth: 0 }}>
          <div className='d-flex align-items-center' style={{ gap: 10 }}>
            <span style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--ink-900)' }}>{group.name}</span>
            <span
              style={{
                fontFamily: FONT_MONO,
                fontSize: 10.5,
                color: 'var(--ink-400)',
                background: 'var(--ink-50)',
                border: '1px solid var(--ink-150)',
                borderRadius: 6,
                padding: '1px 7px',
              }}
            >
              grp-{group.id}
            </span>
            <span
              className='sg-pill sg-pill--nodot'
              data-testid={`job-group-type-${group.id}`}
              style={{
                background: 'var(--ink-50)',
                color: 'var(--ink-500)',
                border: '1px solid var(--ink-150)',
                fontSize: 10,
                fontWeight: 600,
              }}
            >
              {typeLabel(group.typeKey)}
            </span>
          </div>
          {(() => {
            const fan = fanShapeOf(group);
            if (!fan) {
              return group.description ? (
                <div className='sg-small' style={{ color: 'var(--ink-400)', marginTop: 3 }}>
                  {group.description}
                </div>
              ) : null;
            }
            const paired = pairWithLatestRun(group.steps, group.latestRun);
            return (
              <div
                className='d-flex align-items-center flex-wrap'
                style={{ gap: 6, marginTop: 4 }}
                data-testid={`run-fan-${group.id}`}
              >
                <span style={{ fontFamily: FONT_MONO, fontSize: 11.5, color: 'var(--ink-700)' }}>
                  {fan.sourceName}
                  {fan.inventoryName ? (
                    <span style={{ color: 'var(--ink-400)' }}> · {fan.inventoryName}</span>
                  ) : null}
                </span>
                {fan.inPlace ? (
                  <span className='sg-small' style={{ color: 'var(--ink-500)' }}>— in place</span>
                ) : (
                  <>
                    <span style={{ color: 'var(--ink-400)' }}>→</span>
                    {paired.map(({ step, runStep }) => {
                      const st = stepStatusLabel(runStep, !!group.runActive);
                      const pillColors = STATUS[st.semantic] || STATUS.neutral;
                      return (
                        <span
                          key={step.id != null ? step.id : `${step.targetDataSourceId}`}
                          className='d-inline-flex align-items-center'
                          title={runStep && runStep.errorMessage ? `${st.label} — ${runStep.errorMessage}` : st.label}
                          style={{
                            gap: 5,
                            fontFamily: FONT_MONO,
                            fontSize: 11,
                            color: 'var(--ink-700)',
                            background: 'var(--ink-50)',
                            border: '1px solid var(--ink-150)',
                            borderRadius: 6,
                            padding: '1px 8px',
                          }}
                        >
                          <span
                            style={{ width: 7, height: 7, borderRadius: '50%', background: pillColors.dot, flex: 'none' }}
                          />
                          {step.targetDataSourceName || `#${step.targetDataSourceId}`}
                        </span>
                      );
                    })}
                  </>
                )}
              </div>
            );
          })()}
          {/* The latest run's failure is visible on the collapsed card, not tooltip-only.
              One line, clamped; the full text lives in the expanded steps table. */}
          {group.latestRun && group.latestRun.status === 'FAILED' && group.latestRun.errorMessage && (
            <div
              className='sg-small'
              style={{
                color: STATUS.danger.text,
                marginTop: 4,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: 640,
              }}
              title={group.latestRun.errorMessage}
            >
              {group.latestRun.errorMessage}
            </div>
          )}
        </div>
        <div className='ml-auto d-flex align-items-center' style={{ gap: 22, flex: 'none' }}>
          {metaCell('Steps', <span style={{ fontFamily: FONT_MONO, fontWeight: 600 }}>{stepCount}</span>)}
          {metaCell('Schedule', 'Manual')}
          {metaCell('Last run', fmtWhen(group.latestRun && group.latestRun.startedAt))}
          <span
            className='sg-pill sg-pill--nodot'
            style={{ background: pill.bg, color: pill.text, fontSize: 10, fontWeight: 600 }}
            title={group.latestRun && group.latestRun.errorMessage ? group.latestRun.errorMessage : undefined}
          >
            {status.label}
          </span>
          {group.runActive ? (
            <button
              type='button'
              onClick={(e) => {
                e.stopPropagation();
                onCancel();
              }}
              style={{
                border: '1px solid var(--ink-200)',
                background: '#fff',
                cursor: 'pointer',
                fontSize: 11.5,
                fontWeight: 600,
                color: STATUS.danger.text,
                padding: '7px 14px',
                borderRadius: 7,
              }}
            >
              Cancel run
            </button>
          ) : (
            <button
              type='button'
              data-testid={`job-group-run-${group.id}`}
              onClick={(e) => {
                e.stopPropagation();
                onRun();
              }}
              style={{
                border: 'none',
                background: STATUS.info.bg,
                cursor: 'pointer',
                fontSize: 11.5,
                fontWeight: 600,
                color: STATUS.info.dot,
                padding: '7px 14px',
                borderRadius: 7,
              }}
            >
              ▶ Run group
            </button>
          )}
        </div>
      </div>
      {expanded && (
        <StepsTable
          group={group}
          onReplaceSteps={onReplaceSteps}
          onAddStep={onAddStep}
          onEditStep={onEditStep}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      )}
    </div>
  );
}

export default GroupCard;
